/* ==========================================================================
   PAISH WINTER ATELIER — CLIENT JS RUNTIME
   Handles: Snow Drift Canvas, Shade Card Filtering, Frosted Glass Studio Inspector,
   and Real-time Shopify Cart AJAX API integration.
   ========================================================================== */

(function() {
  'use strict';

  // State
  let isSnowActive = false;
  let animId = null;
  let particles = [];
  let currentInspectedProduct = null;
  let selectedVariantId = null;
  let activeShadeFilter = null;
  let activeCategoryFilter = 'all';

  // DOM Elements
  const canvas = document.getElementById('snow-canvas');
  const overlay = document.getElementById('drawer-overlay');
  const inspectDrawer = document.getElementById('inspect-drawer');
  const bagDrawer = document.getElementById('bag-drawer');
  const bagTriggerBtn = document.getElementById('bag-trigger-btn');
  const navBagCount = document.getElementById('nav-bag-count');
  const bagItemsContainer = document.getElementById('bag-items-container');
  const bagTotalVal = document.getElementById('bag-total-val');
  const bagEmptyState = document.getElementById('bag-empty-state');
  const snowToggleBtn = document.getElementById('snow-toggle-btn');
  const snowBtnText = document.getElementById('snow-btn-text');

  /* ==========================================================================
     1. AMBIENT SNOW DRIFT ENGINE
     ========================================================================== */
  if (canvas) {
    const ctx = canvas.getContext('2d');

    function resizeSnowCanvas() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initFlakes();
    }

    function initFlakes() {
      particles = [];
      const count = Math.floor(window.innerWidth < 768 ? 55 : 120);
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2.2 + 0.8,
          density: Math.random() * count,
          speedY: Math.random() * 0.9 + 0.4,
          speedX: Math.random() * 0.5 - 0.25,
          opacity: Math.random() * 0.65 + 0.25
        });
      }
    }

    function drawSnow() {
      if (!isSnowActive) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2, false);
        ctx.fillStyle = `rgba(255, 255, 255, ${p.opacity})`;
        ctx.fill();

        p.y += p.speedY;
        p.x += p.speedX;

        if (p.y > canvas.height) {
          p.y = -5;
          p.x = Math.random() * canvas.width;
        }
        if (p.x > canvas.width) {
          p.x = 0;
        } else if (p.x < 0) {
          p.x = canvas.width;
        }
      }

      animId = requestAnimationFrame(drawSnow);
    }

    window.addEventListener('resize', resizeSnowCanvas);
    resizeSnowCanvas();

    if (snowToggleBtn) {
      snowToggleBtn.addEventListener('click', () => {
        isSnowActive = !isSnowActive;
        if (isSnowActive) {
          document.body.classList.remove('snow-paused');
          snowToggleBtn.classList.add('active');
          if (snowBtnText) snowBtnText.textContent = "Snow: Active";
          drawSnow();
        } else {
          document.body.classList.add('snow-paused');
          snowToggleBtn.classList.remove('active');
          if (snowBtnText) snowBtnText.textContent = "Snow: Paused";
          if (animId) cancelAnimationFrame(animId);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });
    }
  }

  /* ==========================================================================
     2. SHADE CARD & LOOKBOOK FILTER ENGINE
     ========================================================================== */
  window.filterByShade = function(shadeKey) {
    activeShadeFilter = shadeKey;
    
    document.querySelectorAll('.shade-card').forEach(card => {
      if (card.dataset.shade === shadeKey) {
        card.classList.add('active');
      } else {
        card.classList.remove('active');
      }
    });

    const statusBar = document.getElementById('filter-indicator');
    const shadeNameSpan = document.getElementById('filter-shade-name');
    if (statusBar && shadeNameSpan) {
      const activeCard = document.querySelector(`.shade-card[data-shade="${shadeKey}"] .shade-name`);
      shadeNameSpan.textContent = activeCard ? activeCard.textContent.trim() : shadeKey;
      statusBar.style.display = 'flex';
    }

    applyLookbookFilters();

    const lookbookSec = document.getElementById('lookbook');
    if (lookbookSec) {
      lookbookSec.scrollIntoView({ behavior: 'smooth' });
    }
  };

  window.clearShadeFilter = function() {
    activeShadeFilter = null;
    document.querySelectorAll('.shade-card').forEach(card => card.classList.remove('active'));
    const statusBar = document.getElementById('filter-indicator');
    if (statusBar) statusBar.style.display = 'none';
    applyLookbookFilters();
  };

  window.filterCategory = function(catKey, btn) {
    activeCategoryFilter = catKey;
    document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
    if (btn) btn.classList.add('active');
    applyLookbookFilters();
  };

  function applyLookbookFilters() {
    const cards = document.querySelectorAll('.product-card');
    cards.forEach(card => {
      const cCat = card.dataset.category;
      const cShade = card.dataset.shade;

      const matchesCat = (activeCategoryFilter === 'all' || cCat === activeCategoryFilter);
      const matchesShade = (!activeShadeFilter || cShade === activeShadeFilter);

      if (matchesCat && matchesShade) {
        card.classList.remove('hidden');
      } else {
        card.classList.add('hidden');
      }
    });
  }

  /* ==========================================================================
     3. FROSTED GLASS GARMENT INSPECTION STUDIO DRAWER
     ========================================================================== */
  window.openInspector = function(cardElem) {
    if (!cardElem) return;

    let productData = null;
    const jsonScript = cardElem.querySelector('script[type="application/json"]');
    if (jsonScript) {
      try {
        productData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.error("Failed to parse product data JSON", e);
      }
    }

    if (!productData) return;
    currentInspectedProduct = productData;

    // Header & Meta
    const kicker = document.getElementById('drawer-kicker');
    if (kicker) kicker.textContent = productData.edition_text ? productData.edition_text.toUpperCase() : 'PAIISH ATELIER';

    const title = document.getElementById('drawer-title');
    if (title) title.textContent = productData.title;

    const btnPrice = document.getElementById('drawer-btn-price');
    if (btnPrice) btnPrice.textContent = formatMoney(productData.price);

    const desc = document.getElementById('drawer-desc');
    if (desc) {
      if (productData.description && productData.description.trim()) {
        // Strip any HTML tags if present for clean display
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = productData.description;
        desc.textContent = tempDiv.textContent.trim() || tempDiv.innerText.trim();
      } else {
        desc.textContent = "Architectural winter tailoring cut from double-faced cashmere and heavyweight wool.";
      }
    }

    // Main Stage Background Garment Model Image
    const mainImg = document.getElementById('drawer-main-img');
    if (mainImg) {
      mainImg.src = productData.featured_image;
      mainImg.alt = productData.title;
    }

    // Active Gallery & Angle Engine
    window.currentInspectedGallery = (productData.images && productData.images.length > 0) ? productData.images : [productData.featured_image];
    window.currentAngleIndex = 0;

    // Different Angles Row
    const thumbsRow = document.getElementById('drawer-thumbs-row');
    if (thumbsRow) {
      thumbsRow.innerHTML = '';
      window.currentInspectedGallery.forEach((imgUrl, idx) => {
        const angleCard = document.createElement('div');
        angleCard.className = `glass-angle-card ${idx === 0 ? 'active' : ''}`;
        angleCard.innerHTML = `<img src="${imgUrl}" alt="${productData.title} angle ${idx + 1}">`;
        angleCard.onclick = () => {
          setStudioAngle(idx);
        };
        thumbsRow.appendChild(angleCard);
      });
    }

    renderMobileAngleDots();

    // Tailored Size Selector Chips
    const sizePicker = document.getElementById('drawer-size-picker');
    if (sizePicker) {
      sizePicker.innerHTML = '';
      if (productData.variants && productData.variants.length > 0) {
        selectedVariantId = productData.variants[0].id;
        productData.variants.forEach((v, idx) => {
          const chip = document.createElement('button');
          chip.className = `glass-size-chip ${idx === 0 ? 'active' : ''} ${!v.available ? 'disabled' : ''}`;
          chip.textContent = v.title;
          chip.disabled = !v.available;
          chip.onclick = () => {
            selectedVariantId = v.id;
            document.querySelectorAll('#drawer-size-picker .glass-size-chip').forEach(c => c.classList.remove('active'));
            chip.classList.add('active');
            if (btnPrice) btnPrice.textContent = formatMoney(v.price);
          };
          sizePicker.appendChild(chip);
        });
      }
    }

    if (overlay) overlay.classList.add('open');
    if (inspectDrawer) inspectDrawer.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  /* Switch Angle with Smooth Dissolve */
  function setStudioAngle(index) {
    if (!window.currentInspectedGallery || !window.currentInspectedGallery.length) return;
    if (index < 0) index = window.currentInspectedGallery.length - 1;
    if (index >= window.currentInspectedGallery.length) index = 0;

    window.currentAngleIndex = index;
    const imgUrl = window.currentInspectedGallery[index];
    const mainImg = document.getElementById('drawer-main-img');
    if (mainImg) {
      mainImg.style.opacity = '0.3';
      setTimeout(() => {
        mainImg.src = imgUrl;
        mainImg.style.opacity = '1';
      }, 120);
    }

    // Update Desktop Angle Thumbs
    const thumbs = document.querySelectorAll('.glass-angle-card');
    thumbs.forEach((t, i) => {
      if (i === index) t.classList.add('active');
      else t.classList.remove('active');
    });

    renderMobileAngleDots();
  }

  function renderMobileAngleDots() {
    const dotsContainer = document.getElementById('mobile-angle-dots');
    if (!dotsContainer) return;
    dotsContainer.innerHTML = '';
    if (!window.currentInspectedGallery || window.currentInspectedGallery.length <= 1) return;

    window.currentInspectedGallery.forEach((_, idx) => {
      const dot = document.createElement('div');
      dot.className = `mobile-dot ${idx === window.currentAngleIndex ? 'active' : ''}`;
      dot.onclick = () => setStudioAngle(idx);
      dotsContainer.appendChild(dot);
    });
  }

  window.swipeAnglePrev = function(e) {
    if (e) e.stopPropagation();
    setStudioAngle(window.currentAngleIndex - 1);
  };

  window.swipeAngleNext = function(e) {
    if (e) e.stopPropagation();
    setStudioAngle(window.currentAngleIndex + 1);
  };

  /* Mobile Touch Swipe Detection on Stage */
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartTime = 0;
  let isSwiping = false;
  const stageBg = document.getElementById('studio-stage-bg');

  if (stageBg) {
    stageBg.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
      touchStartTime = Date.now();
      isSwiping = false;
    }, { passive: true });

    stageBg.addEventListener('touchmove', (e) => {
      const currentX = e.changedTouches[0].screenX;
      const currentY = e.changedTouches[0].screenY;
      if (Math.abs(currentX - touchStartX) > 20 && Math.abs(currentX - touchStartX) > Math.abs(currentY - touchStartY)) {
        isSwiping = true;
      }
    }, { passive: true });

    stageBg.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      const diffX = touchEndX - touchStartX;
      const diffY = touchEndY - touchStartY;
      const timeDiff = Date.now() - touchStartTime;

      // Swiped horizontally
      if (Math.abs(diffX) > 35 && Math.abs(diffX) > Math.abs(diffY)) {
        if (diffX < 0) {
          // Swiped Left -> Next Angle
          window.swipeAngleNext();
        } else {
          // Swiped Right -> Previous Angle
          window.swipeAnglePrev();
        }
        resetNotchIdleTimer();
      }
    }, { passive: true });
  }

  /* 30-Second Notch Shake Idle Timer */
  let notchIdleTimer = null;

  function resetNotchIdleTimer() {
    clearTimeout(notchIdleTimer);
    removeNotchShake();
    
    // If the panel is collapsed, trigger shake after 30 seconds to nudge the user
    if (inspectDrawer && inspectDrawer.classList.contains('panel-collapsed')) {
      notchIdleTimer = setTimeout(() => {
        triggerNotchShake();
      }, 30000); // exactly 30 seconds
    }
  }

  function triggerNotchShake() {
    const desktopNotch = document.getElementById('desktop-pull-notch');
    const mobileNotch = document.getElementById('mobile-pull-notch');
    if (desktopNotch) desktopNotch.classList.add('nudge-shake');
    if (mobileNotch) mobileNotch.classList.add('nudge-shake');

    // Remove animation class after it plays so it can re-trigger on subsequent idle intervals
    setTimeout(() => {
      removeNotchShake();
      // Set another reminder in 30s if still collapsed
      if (inspectDrawer && inspectDrawer.classList.contains('panel-collapsed')) {
        notchIdleTimer = setTimeout(triggerNotchShake, 30000);
      }
    }, 1400);
  }

  function removeNotchShake() {
    const desktopNotch = document.getElementById('desktop-pull-notch');
    const mobileNotch = document.getElementById('mobile-pull-notch');
    if (desktopNotch) desktopNotch.classList.remove('nudge-shake');
    if (mobileNotch) mobileNotch.classList.remove('nudge-shake');
  }

  /* Click on image stage to toggle collapse */
  window.handleStageClick = function(e) {
    // If this was a swipe gesture, ignore click
    if (isSwiping) return;
    // If user clicked an arrow or interactive control, do nothing
    if (e && e.target && e.target.closest('button')) return;
    
    if (inspectDrawer) {
      if (!inspectDrawer.classList.contains('panel-collapsed')) {
        toggleGlassPanelCollapse(true);
      }
    }
  };

  /* Toggle Clear View / Collapse Panel */
  window.toggleGlassPanelCollapse = function(forceCollapse) {
    if (!inspectDrawer) return;

    let shouldCollapse;
    if (typeof forceCollapse === 'boolean') {
      shouldCollapse = forceCollapse;
      if (shouldCollapse) inspectDrawer.classList.add('panel-collapsed');
      else inspectDrawer.classList.remove('panel-collapsed');
    } else {
      shouldCollapse = inspectDrawer.classList.toggle('panel-collapsed');
    }
    
    const panelToggleLabel = document.getElementById('panel-toggle-label');

    if (shouldCollapse) {
      if (panelToggleLabel) panelToggleLabel.textContent = "Expand Details ▴";
      resetNotchIdleTimer();
    } else {
      if (panelToggleLabel) panelToggleLabel.textContent = "Hide Details ▾";
      clearTimeout(notchIdleTimer);
      removeNotchShake();
    }
  };

  window.closeInspector = function() {
    clearTimeout(notchIdleTimer);
    removeNotchShake();
    if (overlay) overlay.classList.remove('open');
    if (inspectDrawer) {
      inspectDrawer.classList.remove('open');
      inspectDrawer.classList.remove('panel-collapsed');
    }
    document.body.style.overflow = '';
  };

  /* ==========================================================================
     4. SHOPIFY AJAX CART ENGINE
     ========================================================================== */
  window.closeAnyOpenDrawer = function() {
    closeInspector();
    closeBagDrawer();
  };

  window.openBagDrawer = function() {
    const overlayEl = document.getElementById('drawer-overlay');
    const bagDrawerEl = document.getElementById('bag-drawer');
    const inspectDrawerEl = document.getElementById('inspect-drawer');

    // Close studio inspector if open so they don't collide
    if (inspectDrawerEl) {
      inspectDrawerEl.classList.remove('open');
      inspectDrawerEl.classList.remove('panel-collapsed');
    }

    refreshCart();
    if (overlayEl) overlayEl.classList.add('open');
    if (bagDrawerEl) bagDrawerEl.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  if (bagTriggerBtn) {
    bagTriggerBtn.addEventListener('click', openBagDrawer);
  }

  window.closeBagDrawer = function() {
    const overlayEl = document.getElementById('drawer-overlay');
    const bagDrawerEl = document.getElementById('bag-drawer');
    if (overlayEl) overlayEl.classList.remove('open');
    if (bagDrawerEl) bagDrawerEl.classList.remove('open');
    document.body.style.overflow = '';
  };

  window.addToBagFromDrawer = function() {
    if (!selectedVariantId) {
      if (currentInspectedProduct && currentInspectedProduct.variants && currentInspectedProduct.variants.length > 0) {
        selectedVariantId = currentInspectedProduct.variants[0].id;
      } else {
        alert("Please select a size first.");
        return;
      }
    }

    const btn = document.querySelector('#inspect-drawer .glass-primary-cta');
    const originalText = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Allocating to Bag...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: selectedVariantId,
        quantity: 1
      })
    })
    .then(res => {
      if (!res.ok) {
        return res.json().then(errData => { throw new Error(errData.description || 'Could not allocate item'); });
      }
      return res.json();
    })
    .then(item => {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = originalText || `Add to Atelier Bag &mdash; <span id="drawer-btn-price">${formatMoney(item.price)}</span>`;
      }
      closeInspector();
      openBagDrawer();
    })
    .catch(err => {
      console.error("Cart addition failed:", err);
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = originalText;
      }
      alert(err.message || 'Item allocation failed. Please try again.');
    });
  };

  /* Quick Add direct from Lookbook Card */
  window.quickAddProductCard = function(e, btnElem) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const card = btnElem.closest('.product-card');
    if (!card) return;

    let productData = null;
    const jsonScript = card.querySelector('script[type="application/json"]');
    if (jsonScript) {
      try {
        productData = JSON.parse(jsonScript.textContent);
      } catch (err) {
        console.error("Failed to parse card JSON:", err);
      }
    }

    if (!productData || !productData.variants || productData.variants.length === 0) {
      // Fallback: open studio drawer so user can select
      openInspector(card);
      return;
    }

    // Find first available variant or fallback to first variant
    const variant = productData.variants.find(v => v.available) || productData.variants[0];
    if (!variant || !variant.id) {
      openInspector(card);
      return;
    }

    const origText = btnElem.innerHTML;
    btnElem.disabled = true;
    btnElem.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variant.id,
        quantity: 1
      })
    })
    .then(res => {
      if (!res.ok) {
        return res.json().then(errData => { throw new Error(errData.description || 'Could not add to bag'); });
      }
      return res.json();
    })
    .then(item => {
      btnElem.disabled = false;
      btnElem.innerHTML = '✓ Added';
      setTimeout(() => {
        btnElem.innerHTML = origText;
      }, 1800);
      openBagDrawer();
    })
    .catch(err => {
      console.error("Quick add failed:", err);
      btnElem.disabled = false;
      btnElem.innerHTML = origText;
      alert(err.message || 'Could not add item to bag.');
    });
  };

  function refreshCart() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        renderCartUI(cart);
      })
      .catch(err => console.error("Failed to load cart", err));
  }

  function renderCartUI(cart) {
    const navBagCountEl = document.getElementById('nav-bag-count');
    const bagItemsContainerEl = document.getElementById('bag-items-container');
    const bagTotalValEl = document.getElementById('bag-total-val');
    const bagEmptyStateEl = document.getElementById('bag-empty-state');

    if (navBagCountEl) navBagCountEl.textContent = cart.item_count;

    if (!bagItemsContainerEl || !bagTotalValEl) return;

    if (cart.item_count === 0) {
      if (bagEmptyStateEl) bagEmptyStateEl.style.display = 'block';
      bagItemsContainerEl.innerHTML = '';
      if (bagEmptyStateEl) bagItemsContainerEl.appendChild(bagEmptyStateEl);
      bagTotalValEl.textContent = '$0';
      return;
    }

    if (bagEmptyStateEl) bagEmptyStateEl.style.display = 'none';
    bagItemsContainerEl.innerHTML = '';

    cart.items.forEach(item => {
      const itemRow = document.createElement('div');
      itemRow.className = 'bag-item-card';
      const imgUrl = (item.featured_image && (item.featured_image.url || item.featured_image)) || item.image || '';
      itemRow.innerHTML = `
        <img src="${imgUrl}" class="bag-item-img" alt="${item.title}">
        <div class="bag-item-details">
          <div class="bag-item-title">${item.product_title}</div>
          <div class="bag-item-meta">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
          <div class="bag-item-price">${formatMoney(item.final_line_price)}</div>
          <div class="bag-item-qty">
            <button class="bag-qty-btn" onclick="updateItemQuantity('${item.key}', ${item.quantity - 1})">-</button>
            <span class="mono-meta">${item.quantity}</span>
            <button class="bag-qty-btn" onclick="updateItemQuantity('${item.key}', ${item.quantity + 1})">+</button>
          </div>
        </div>
        <button class="bag-item-remove" onclick="updateItemQuantity('${item.key}', 0)" title="Remove item">✕</button>
      `;
      bagItemsContainerEl.appendChild(itemRow);
    });

    bagTotalValEl.textContent = formatMoney(cart.total_price);
  }

  window.updateItemQuantity = function(lineKey, newQty) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: newQty
      })
    })
    .then(res => res.json())
    .then(cart => {
      renderCartUI(cart);
    })
    .catch(err => console.error("Quantity change failed", err));
  };

  window.proceedToAllocationCheckout = function() {
    window.location.href = '/checkout';
  };

  /* Helper to format price in dollars */
  function formatMoney(cents) {
    if (typeof cents === 'string' && cents.startsWith('$')) return cents;
    const dollars = (cents / 100).toFixed(0);
    return `$${Number(dollars).toLocaleString()}`;
  }

  // Initial cart count check
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      if (navBagCount) navBagCount.textContent = cart.item_count;
    })
    .catch(() => {});

  /* Mobile Nav Drawer */
  const mobileToggle = document.getElementById('mobile-toggle');
  const mobileNavDrawer = document.getElementById('mobile-nav-drawer');

  if (mobileToggle && mobileNavDrawer) {
    mobileToggle.addEventListener('click', () => {
      mobileNavDrawer.classList.toggle('open');
    });
  }

  window.closeMobileMenu = function() {
    if (mobileNavDrawer) mobileNavDrawer.classList.remove('open');
  };

})();
